import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faCircleMinus, faEllipsis, faPlus, faArrowDownShortWide, faArrowDownWideShort, faSearch } from "@fortawesome/pro-regular-svg-icons";
import InputField from "../../components/InputField";
import CustomDropdown from "../../components/CustomDropdown";
import Modal from "../../components/Modal";
import SelectDropdown from "../../components/SelectDropdown";
import { useAuth } from "../../providers/AuthContext";
import { getAllThirdPartySystems } from "../../api/Resources";
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import { useTranslation } from "react-i18next";

const CustomerThirdPartyRef = ({thirdPartySystems, setThirdPartySystems, thirdPartySystemMapping, setThirdPartySystemMapping, addModal, setAddModal, addTPRState, setAddTPRState, confirmState, setConfirmState, setHasChanges }) => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();


    useEffect(() => {
        handleGetAllThirdPartySystems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [])

    useEffect(() => {
        if (!addModal) {
            setAddTPRState({ system: "", id: "" });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [addModal]);

    const handleGetAllThirdPartySystems = async () => {
        try {
            const accessToken = await getAccessToken();
            const systems = await getAllThirdPartySystems(accessToken);
            setThirdPartySystems(systems);
        } catch (error) {
            toast.error(t("toast.failedFetch", { entity: t("entity.thirdPartySystems") }), error);
        }
    };

    const addTPRef = (system, id) => {
        setHasChanges(true);
        const isExisting = thirdPartySystemMapping.find((s) => s.thirdPartySystemName === system && s.thirdPartySystemExternalId === id);
        if (isExisting && !isExisting.isDeleted) {
            toast.error(t("toast.alreadyExists", { entity: t("entity.thirdPartyReference") }));
        } else if (isExisting && isExisting.isDeleted) {
            setThirdPartySystemMapping((s) => s.map((m) => {
                if (m.thirdPartySystemName === system && m.thirdPartySystemExternalId === id) {
                    return { ...m, isDeleted: false };
                }
                return m;
            }));
            setAddModal(false);
        } else {
            const thirdPartySystem = thirdPartySystems.find((s) => s.name === system);
            setThirdPartySystemMapping((s) => ([...s, { thirdPartySystemName: system, thirdPartySystemExternalId: id, isNew: true, thirdPartySystemType: thirdPartySystem.type, thirdPartySystemPublicId: thirdPartySystem.thirdPartySystemPublicId }]))
            setAddModal(false);
        }
    };

    const removeTPRef = (system, id) => {
        setHasChanges(true);
        setThirdPartySystemMapping((prev) =>
            prev.map((mapping) => {
                // Om det är en ny post som inte har sparats än (isNew), ta bort den helt.
                if (mapping.thirdPartySystemName === system && mapping.thirdPartySystemExternalId === id && mapping.isNew) {
                    return null; // Vi kommer filtrera bort denna senare
                }

                // Annars, markera posten som isDeleted
                if (mapping.thirdPartySystemName === system && mapping.thirdPartySystemExternalId === id) {
                    return { ...mapping, isDeleted: true };
                }

                return mapping;
            }).filter(Boolean) // Filtrera bort alla null (dvs. nyskapade poster som tagits bort innan sparande)
        );
    };

    return (
        <>
            <div className="flex w-full">

                <ThirdPartySystemTable removeTPRef={removeTPRef}
                    setConfirmState={setConfirmState}
                    thirdPartySystemMapping={thirdPartySystemMapping}
                    addModal={addModal} setAddModal={setAddModal}
                    thirdPartySystems={thirdPartySystems}
                    addTPRState={addTPRState}
                    setAddTPRState={setAddTPRState}
                    addTPRef={addTPRef} />

            </div>
            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title ? confirmState.title : t("confirm.deleteTitle", { name: t("entity.thirdPartyReference") })}
                onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
                onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
            />

        </>
    );
};

export default CustomerThirdPartyRef;


const ThirdPartySystemModal = ({ addModal, setAddModal, thirdPartySystems, addTPRState, setAddTPRState, addTPRef }) => {
    const { t } = useTranslation();
    return (
        <Modal isOpen={addModal} setIsOpen={() => setAddModal(false)}>
            <div className="flex flex-col justify-start gap-4 items-start">
                <h3>{t("entity.thirdPartyReference")}</h3>

                <SelectDropdown
                    options={thirdPartySystems.map(system => ({
                        value: system,
                        label: system.name
                    }))}
                    onChange={(e) => { setAddTPRState((s) => ({ ...s, system: e.label })); }}
                    placeholder={t("dropdown.select")}
                    label={t("entity.system")}
                    isMulti={false}
                />

                <InputField
                    label={t("entity.thirdPartyReference")}
                    placeholder={t("table.customerId")}
                    value={addTPRState.id || ''}
                    onChange={(e) => {
                        const newId = e.target?.value;
                        setAddTPRState((s) => ({ ...s, id: newId }));
                    }}
                />

                <div className="flex gap-2 w-full justify-end">
                    <button className="btn btn-outline-primary" onClick={() => setAddModal(false)}>{t("common.cancel")}</button>

                    <button className="btn btn-primary" disabled={!addTPRState.id} onClick={() => { addTPRef(addTPRState.system, addTPRState.id) }}>{t("common.add")}</button>
                </div>
            </div>
        </Modal>
    );
};

const ThirdPartySystemTable = ({ addModal, setAddModal, thirdPartySystems, addTPRState, setAddTPRState, addTPRef, thirdPartySystemMapping, setConfirmState, removeTPRef }) => {
    const { t } = useTranslation();
    const [filteredMappings, setFilteredMappings] = useState(thirdPartySystemMapping);
    const [sortColumn, setSortColumn] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [search, setSearch] = useState('');

    useEffect(() => {
        applyFilterAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [search, thirdPartySystemMapping, sortColumn, sortOrder]);

    const applyFilterAndSort = () => {
        let filtered = thirdPartySystemMapping.filter((i) => !i.isDeleted);

        // Filter based on search
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (item) =>
                    item.thirdPartySystemName.toLowerCase().includes(searchLower) ||
                    item.thirdPartySystemType.toLowerCase().includes(searchLower) ||
                    item.thirdPartySystemExternalId.toLowerCase().includes(searchLower)
            );
        }

        // Sort based on selected column
        const sorted = [...filtered].sort((a, b) => {
            let aValue = a[sortColumn]?.toString().toLowerCase();
            let bValue = b[sortColumn]?.toString().toLowerCase();

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredMappings(sorted);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn === column) {
            return sortOrder === 'asc' ? faArrowDownShortWide : faArrowDownWideShort;
        }
        return null;
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row justify-between items-center">
                <h4 className="mb-2">{t("thirdParty.references")}</h4>
                <div className="flex justify-end">
                    <div className="mb-2">
                        <InputField icon={faSearch} placeholder={t("search.thirdPartyRef")} value={search} onChange={(v) => { setSearch(v.target.value) }} />
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                <table className="table-default w-full">
                    <thead>
                        <tr>
                            <th className="cursor-pointer" onClick={() => handleSort('thirdPartySystemName')}>
                                {t("entity.system")} {getSortIcon('thirdPartySystemName') && <FontAwesomeIcon icon={getSortIcon('thirdPartySystemName')} className="w-4 pl-2" />}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort('thirdPartySystemType')}>
                                {t("common.type")} {getSortIcon('thirdPartySystemType') && <FontAwesomeIcon icon={getSortIcon('thirdPartySystemType')} className="w-4 pl-2" />}
                            </th>
                            <th className="cursor-pointer" onClick={() => handleSort('thirdPartySystemExternalId')}>
                                {t("table.customerId")} {getSortIcon('thirdPartySystemExternalId') && <FontAwesomeIcon icon={getSortIcon('thirdPartySystemExternalId')} className="w-4 pl-2" />}
                            </th>
                            <th>
                                <div className="flex w-full justify-end items-center">
                                    <button
                                        className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                                        onClick={() => { setAddModal(true) }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} /> {t("common.addNew")}
                                    </button>
                                    <ThirdPartySystemModal addModal={addModal} setAddModal={setAddModal} thirdPartySystems={thirdPartySystems} addTPRState={addTPRState} setAddTPRState={setAddTPRState} addTPRef={addTPRef} />
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMappings.map((tbsm, index) => (
                            <tr key={index}>
                                <td>{tbsm.thirdPartySystemName}</td>
                                <td>{tbsm.thirdPartySystemType}</td>
                                <td>{tbsm.thirdPartySystemExternalId}</td>
                                <td className="text-center flex justify-end">
                                    <CustomDropdown isRight={true}
                                        ContentComponent={() => (
                                            <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                                <button
                                                    className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                                    onClick={() => {
                                                        setConfirmState((s) => ({
                                                            ...s,
                                                            isOpen: true,
                                                            id: tbsm.thirdPartySystemExternalId,
                                                            title: t("thirdParty.deleteWithId", { id: tbsm.thirdPartySystemExternalId }),
                                                            confirm: () => { removeTPRef(tbsm.thirdPartySystemName, tbsm.thirdPartySystemExternalId) }
                                                        }));
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                            <FontAwesomeIcon icon={faEllipsis} />
                                        </div>
                                    </CustomDropdown>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
